import { useState, useEffect, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { ff, flags } from 'functions/roleBasedAccessUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SolidButton from 'components/Buttons/SolidButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function AthleteCreateView() {

    const navigate = useNavigate()
    const location = useLocation()

    const { createAccountData } = location.state || [];

    const { ShowModal } = useContext(ModalContext)

    const userId = useSelector((state) => state.user.id)
    const coachFirstName = useSelector((state) => state.user.firstName)
    const coachLastName = useSelector((state) => state.user.lastName)
    const organizationId = useSelector((state) => state.user.organizationId)
    const organizationName = useSelector((state) => state.user.organizationName)
    const organizationCode = useSelector((state) => state.user.organizationCode)
    const requireEmailToCreateAthleteAccount = useSelector((state) => state.user.requireEmailToCreateAthleteAccount)

    const emailRequiredWhenCreatingAthletes = ff(flags.emailRequiredWhenCreatingAthletes, { requireEmailToCreateAthleteAccount: requireEmailToCreateAthleteAccount })

    var defaultAthleteInfoObject = {
        firstName: '',
        firstNameValidationError: '',
        lastName: '',
        lastNameValidationError: '',
        email: '',
        emailValidationError: '',
        sendInvite: emailRequiredWhenCreatingAthletes,
        showSendInviteTooltip: false,
        trainingPeaksUserId: '',
    }

    const [athleteInfo, setAthleteInfo] = useState([JSON.parse(JSON.stringify(defaultAthleteInfoObject))])
    const [invitesLoading, setInvitesLoading] = useState(false)
    const [errorText, setErrorText] = useState('')

    const ProcessCreateAccountData = (createAccountData) => {

        var newAthleteInfo = []

        createAccountData.forEach(item => {
            newAthleteInfo.push({
                ...JSON.parse(JSON.stringify(defaultAthleteInfoObject)),
                firstName: item.firstName.trim(),
                lastName: item.lastName.trim(),
                email: item.email.trim().toLowerCase(),
                trainingPeaksUserId: item.trainingPeaksUserId
            })
        })

        setAthleteInfo(newAthleteInfo)
    }

    const ShowAthleteInviteImportModal = () => {
        const modalData = {
            key: ModalKey.FileImport,
            size: 'third',
            titleText: 'Upload a CSV file of athlete emails',
            fileFormatDescriptionText: 'The CSV file should contain a list of emails each separated by a newline',
            exampleFileObject: (
                <div className='w-full bg-gray-100 pt-6 px-4 pb-4 rounded-md relative'>
                    <p>firstName,lastName,athlete1@gmail.com</p>
                    <p>firstName,lastName,athlete2@gmail.com</p>
                    <p>firstName,lastName,athlete3@gmail.com</p>

                    <div className='absolute top-0 left-0 text-xs text-gray-400 font-semibold py-1 px-2'>example_athletes.csv</div>
                </div>
            ),
            buttonText: 'Import Athletes',
            buttonAction: (fileData) => ProcessCSVFile(fileData)
        }

        ShowModal(modalData)
    }

    const ProcessCSVFile = (fileData) => {

        const reader = new FileReader()
        
        reader.onload = (e) => {

            const text = e.target.result
            const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n') // Normalize line endings (handle Windows \r\n, Mac \r, and Unix \n)
            
            // console.log(normalizedText)

            // Split by newline and filter out empty lines
            const csvFileDataArray = normalizedText.split('\n').map(line => line.trim()).filter(line => line.length > 0)
            
            // console.log(csvFileDataArray)
            
            setAthleteInfo(csvFileDataArray.map(line => ({
                ...JSON.parse(JSON.stringify(defaultAthleteInfoObject)),
                firstName: line.split(',')[0]?.trim(),
                lastName: line.split(',')[1]?.trim(),
                email: line.split(',')[2] !== undefined ? line.split(',')[2]?.trim().toLowerCase() : '',
                emailValidationError: ''
            })))
        }
        
        reader.readAsText(fileData)

    }

    const HandleAthleteFirstNameChange = (index, firstName) => {
        var targetAthleteObject = athleteInfo[index]

        targetAthleteObject.firstName = firstName.trim().replace(/[^a-zA-Z]/g, '')
        targetAthleteObject.firstNameValidationError = ''

        setAthleteInfo(athleteInfo.map((a, i) =>
            i === index
                ? targetAthleteObject
                : a
        ))
    }

    const HandleAthleteLastNameChange = (index, lastName) => {
        var targetAthleteObject = athleteInfo[index]

        targetAthleteObject.lastName = lastName.trim().replace(/[^a-zA-Z]/g, '')
        targetAthleteObject.lastNameValidationError = ''

        setAthleteInfo(athleteInfo.map((a, i) =>
            i === index
                ? targetAthleteObject
                : a
        ))
    }

    const HandleAthleteEmailChange = (index, email) => {
        var targetAthleteObject = athleteInfo[index]

        targetAthleteObject.email = email.trim().toLowerCase()
        targetAthleteObject.emailValidationError = ''

        setAthleteInfo(athleteInfo.map((e, i) =>
            i === index
                ? targetAthleteObject
                : e
        ))
    }

    const HandleSendInviteToggle = (index, sendInvite) => {
        var targetAthleteObject = athleteInfo[index]

        targetAthleteObject.sendInvite = sendInvite

        setAthleteInfo(athleteInfo.map((a, i) =>
            i === index
                ? targetAthleteObject
                : a
        ))
    }

    const HandleSendInviteTooltipToggle = (index, showSendInviteTooltip) => {
        var targetAthleteObject = athleteInfo[index]

        targetAthleteObject.showSendInviteTooltip = showSendInviteTooltip

        setAthleteInfo(athleteInfo.map((a, i) =>
            i === index
                ? targetAthleteObject
                : a
        ))
    }

    const HandleRemoveAthleteEmail = (index) => {
        if (athleteInfo.length <= 1) return
        setAthleteInfo(athleteInfo.filter((_, i) => i !== index))
    }

    const ValidateAthleteDataOnFrontend = () => {

        var allDataValid = true

        var athletesWithValidationErrors = JSON.parse(JSON.stringify(athleteInfo))

        // Reset all validation errors first

        athletesWithValidationErrors.forEach(athleteObj => {
            athleteObj.firstNameValidationError = ''
            athleteObj.lastNameValidationError = ''
            athleteObj.emailValidationError = ''
        })

        setErrorText('')

        // Validate inputs

        athletesWithValidationErrors.map(athleteObj => {

            const trimmedFirstName = athleteObj?.firstName?.trim()
            const trimmedLastName = athleteObj?.lastName?.trim()
            const trimmedEmail = athleteObj?.email?.trim()

            if (trimmedFirstName === '') {
                allDataValid = false
                athleteObj.firstNameValidationError = 'First name is required'
            }
            
            if (trimmedLastName === '') {
                allDataValid = false
                athleteObj.lastNameValidationError = 'Last name is required'
            }

            if (emailRequiredWhenCreatingAthletes === true) {
                if (
                    trimmedEmail === ''
                    || !IsValidEmailRegex(trimmedEmail)
                ) {
                    allDataValid = false
                    athleteObj.emailValidationError = 'Invalid email address'
                }
            }
        })

        // Check for duplicate emails
        
        const emailCounts = {}

        athletesWithValidationErrors.forEach(athleteObj => {
            const trimmedEmail = athleteObj?.email?.trim().toLowerCase()

            if (trimmedEmail !== undefined && trimmedEmail !== '') {
                emailCounts[trimmedEmail] = (emailCounts[trimmedEmail] || 0) + 1
            }
        })

        athletesWithValidationErrors.forEach(athleteObj => {
            const trimmedEmail = athleteObj?.email?.trim().toLowerCase()

            if (trimmedEmail !== undefined && trimmedEmail !== '' && emailCounts[trimmedEmail] > 1) {
                allDataValid = false
                // Only set duplicate error if email is valid (don't overwrite invalid email format errors)
                if (!athleteObj.emailValidationError) {
                    athleteObj.emailValidationError = 'Duplicate email address'
                }
            }

        })

        setAthleteInfo(athletesWithValidationErrors)

        return allDataValid
    }

    const ValidateAthleteDataOnBackend = async () => {

        const emails = athleteInfo.map(athleteObj => athleteObj.email)

        try {
            const jsonBody = {
                coachId: userId,
                organizationId: organizationId,
                emails: JSON.stringify(emails)
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('EMAIL', 'GET', '/create_athlete_email_validation', jsonBody)

            // console.log(result)

            if (result.data.status === "failure") {
                setErrorText('Failed to validate athlete emails. Please try again.')
                console.error(result)
                return false
            }

            var existingEmails = result.data.data

            if (existingEmails.length > 0) {

                existingEmails.forEach(email => {
                    athleteInfo.forEach(athleteObj => {
                        if (athleteObj.email === email) {
                            athleteObj.emailValidationError = 'Account with email already exists'
                        }
                    })
                })

                setErrorText('Cannot create accounts with emails that already exist. Try inviting existing accounts instead.')

                setAthleteInfo(athleteInfo)

                return false
            }

            // console.log(existingEmails)

        } catch (error) {
            setErrorText('Failed to validate athlete emails. Please try again.')
            console.error(error)
            return false
        }

        return true

    }

    const IsValidEmailRegex = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    const OnCreateAthletes = async () => {

        setInvitesLoading(true)

        if (athleteInfo.length === 0) {
            setInvitesLoading(false)
            return
        }

        if (ValidateAthleteDataOnFrontend() === false) {
            setInvitesLoading(false)
            return
        }

        if (emailRequiredWhenCreatingAthletes === true) {
            if (await ValidateAthleteDataOnBackend() === false) {
                setInvitesLoading(false)
                return
            }
        }

        try {
            const jsonBody = {
                coachId: userId,
                coachFirstName: coachFirstName,
                coachLastName: coachLastName,
                organizationId: organizationId,
                organizationName: organizationName,
                organizationCode: organizationCode,
                athleteInfo: athleteInfo
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('EMAIL', 'POST', '/create_athletes_in_organization', jsonBody)

            // console.log(result)

            if (result.data.status === "failure") {
                ShowFailureModal()
                setInvitesLoading(false)
                console.error(result)
                return
            }

            setInvitesLoading(false)

            // Navigate to success page with success message
            
            const navigationProps = {
                state: {
                    successMessage: 'Athletes have been created'
                }
            }

            navigate('/success', navigationProps)

        } catch (error) {
            ShowFailureModal()
            setInvitesLoading(false)
            console.error(error)
        }
    }

    const ShowFailureModal = () => {
        const modalData = {
            key: ModalKey.Action,
            size: 'quarter',
            icon: <ExclamationTriangleIcon className="h-8 w-8 text-white" aria-hidden="true" />,
            titleText: 'Error!',
            bodyText: [
                'Failed to send athlete invites. Please try again.',
                'If the problem persists, please contact support@kinesisintegrated.com'
            ],
            buttonText: 'Close'
        }

        ShowModal(modalData)
    }

    useEffect(() => {
        if (createAccountData !== null && createAccountData !== undefined && createAccountData.length > 0) {
            ProcessCreateAccountData(createAccountData)
        }
    }, [createAccountData])

    return (
        <ScreenFrame>
            <div className='space-y-1 pb-2'>
                <div className='flex items-center gap-x-2'>
                    <div className='space-y-1 flex-grow'>
                        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2">
                            Enter Athlete Information Below
                        </h1>
                    </div>

                    <div className='w-60'>
                        <SolidButton
                            text="Import Athletes"
                            action={ShowAthleteInviteImportModal}
                            className='shadow-md'
                        />
                    </div>
                </div>
            </div>

            {athleteInfo.map((athleteObj, index) => (
                <div
                    key={index}
                    className='flex items-center gap-x-4'
                >
                    <div className='flex-grow grid grid-cols-4 gap-x-2'>
                        <div className='col-span-1 relative'>
                            <input
                                type="text"
                                className={`
                                    w-full py-2 px-4 rounded-md border border-gray-200 placeholder-gray-500 shadow-md
                                    focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                    ${athleteObj.firstNameValidationError.length > 0 ? 'bg-red-50' : 'bg-white'}
                                `}
                                placeholder='Enter First Name Here'
                                value={athleteObj.firstName}
                                onChange={(event) => HandleAthleteFirstNameChange(index, event.target.value)}
                            />

                            {athleteObj.firstNameValidationError && (
                                <div className='absolute bottom-3 right-4 pl-2 bg-red-50 text-red-600 text-sm z-10'>
                                    {athleteObj.firstNameValidationError}
                                </div>
                            )}
                        </div>

                        <div className='col-span-1 relative'>
                            <input
                                type="text"
                                className={`
                                    w-full py-2 px-4 rounded-md border border-gray-200 placeholder-gray-500 shadow-md
                                    focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                    ${athleteObj.lastNameValidationError.length > 0 ? 'bg-red-50' : 'bg-white'}
                                `}
                                placeholder='Enter Last Name Here'
                                value={athleteObj.lastName}
                                onChange={(event) => HandleAthleteLastNameChange(index, event.target.value)}
                            />

                            {athleteObj.lastNameValidationError && (
                                <div className='absolute bottom-3 right-4 pl-2 bg-red-50 text-red-600 text-sm z-10'>
                                    {athleteObj.lastNameValidationError}
                                </div>
                            )}
                        </div>

                        <div className='col-span-2 relative'>
                            <input
                                type="text"
                                className={`
                                    w-full py-2 px-4 rounded-md border border-gray-200 placeholder-gray-500 shadow-md
                                    focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400
                                    ${athleteObj.emailValidationError.length > 0 ? 'bg-red-50' : 'bg-white'}
                                `}
                                placeholder={emailRequiredWhenCreatingAthletes === true ? 'Enter Email Here' : 'Enter Email Here (optional)'}
                                value={athleteObj.email}
                                onChange={(event) => HandleAthleteEmailChange(index, event.target.value)}
                            />

                            {athleteObj.emailValidationError && (
                                <div className='absolute bottom-3 right-4 pl-2 bg-red-50 text-red-600 text-sm z-10'>
                                    {athleteObj.emailValidationError}
                                </div>
                            )}
                        </div>
                    </div>

                    <div 
                        className='relative flex items-center bg-white py-2 pl-3 pr-2 gap-x-2 rounded-md cursor-pointer shadow-md'
                        onClick={() => HandleSendInviteToggle(index, !athleteObj.sendInvite)}
                    >
                        <input
                            type="checkbox"
                            className="h-4 w-4 border-gray-300 rounded focus:ring-orange-400 cursor-pointer"
                            checked={athleteObj.sendInvite}
                            onChange={(event) => HandleSendInviteToggle(index, event.target.checked)}
                        />

                        <label className='text-sm text-gray-700 truncate'>Send Invite</label>

                        <InformationCircleIcon
                            onMouseEnter={() => HandleSendInviteTooltipToggle(index, true)}
                            onMouseLeave={() => HandleSendInviteTooltipToggle(index, false)}
                            className='h-4 w-4 text-gray-600 cursor-pointer'
                        />

                        {athleteObj.showSendInviteTooltip && (
                            <div className="absolute top-full left-1/2 mt-2 w-40 -translate-x-1/2 transform rounded-md bg-white border border-gray-200 p-4 text-xs shadow-xl z-30">
                                Enabling this will also send an email with instructions on how to sign in to this created account. Invites can always be sent later.
                            </div>
                        )}
                    </div>

                    <XMarkIcon
                        className='h-5 w-5 text-gray-400 cursor-pointer'
                        onClick={() => HandleRemoveAthleteEmail(index)}
                    />
                </div>
            ))}

            <div>
                <p className='-mt-1 text-sm text-gray-500'>{athleteInfo.length} athlete{athleteInfo.length === 1 ? '' : 's'} added</p>
            </div>

            <div className='pt-2 w-2/3 mx-auto space-y-4'>

                {errorText && <p className='text-center text-sm text-red-600'>{errorText}</p>}

                <div className='grid grid-cols-2 gap-x-4'>
                    <SolidButton
                        text="Add Athlete"
                        action={() => setAthleteInfo([...athleteInfo, { ...defaultAthleteInfoObject }])}
                        className='shadow-md'
                    />

                    <BrandColorButton
                        text="Create Athletes"
                        action={OnCreateAthletes}
                        isLoading={invitesLoading}
                        className='shadow-md'
                    />
                </div>
            </div>
        </ScreenFrame>
    )
}
